import { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LogOut, Pencil, Copy, Trash2, Terminal, Plus } from 'lucide-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { id as makeId, tx } from '@instantdb/react';

import { CompoundSwitcher } from '@/components/CompoundSwitcher';
import { EditCompoundSheet } from '@/components/EditCompoundSheet';
import { useCompounds } from '@/lib/state/compounds';
import { db } from '@/lib/db/instant';
import { useServerQuery } from '@/lib/db/useServerQuery';
import { queryServer, transactServer, transactCreateServer, matchesSavedFields } from '@/lib/db/server';
import { useDoseSubmission } from '@/lib/forms/useDoseSubmission';

export const Route = createFileRoute('/_tabs/profile')({
  component: ProfileScreen,
});

function Row({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row items-center justify-between border-b border-border/60 py-2.5">
      <Text className="text-sm text-textDim">{label}</Text>
      <Text className="text-sm text-text">{value}</Text>
    </View>
  );
}

function ProfileScreen() {
  const { active, userCompounds, setActiveId, deleteCompound } = useCompounds();
  const { user } = db.useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const { submitting, error, submit } = useDoseSubmission(() => {});

  if (!active) return null;

  const hasMany = userCompounds.length > 1;

  const openEdit = (compoundId: string) => {
    setActiveId(compoundId);
    setEditing(true);
  };

  const handleLogout = () => {
    void submit(async () => { await db.auth.signOut(); });
  };

  const handleAddCompound = () => {
    navigate({ to: '/onboarding' });
  };

  const handleDeleteCompound = (compoundId: string) => {
    const compound = userCompounds.find((uc) => uc.id === compoundId);
    if (!compound) return;

    const willRemoveLast = userCompounds.length === 1;
    const ok = window.confirm(
      `Remove ${compound.preset.shortCode}? This also removes its dose log${
        willRemoveLast ? ' and returns you to setup' : ''
      }.`,
    );
    if (!ok) return;

    void submit(async () => {
      await deleteCompound(compoundId);
      if (willRemoveLast) await navigate({ to: '/onboarding', replace: true });
    });
  };

  return (
    <SafeAreaView className="flex-1 bg-bg" edges={['top']}>
      <View className="flex-row items-center justify-between px-5 pb-3 pt-2">
        <View>
          <Text className="text-xl font-semibold text-text">Profile</Text>
          <Text className="mt-0.5 text-xs text-textMuted">Compound, schedule, account</Text>
        </View>
        <CompoundSwitcher />
      </View>

      {error && <Text role="alert" className="px-5 py-2 text-sm text-danger">{error}</Text>}
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 48 }}
        showsVerticalScrollIndicator={false}
      >
        <View className="flex-row items-center justify-between pt-2">
          <View>
            <Text className="text-[10px] font-medium uppercase tracking-wider text-textMuted">
              {hasMany ? 'Currently showing' : 'Compound'}
            </Text>
            <Text className="mt-0.5 text-base font-medium text-text">{active.preset.name}</Text>
            <Text className="text-xs text-textMuted capitalize">{active.preset.kind}</Text>
          </View>
          <Pressable
            disabled={submitting}
            onPress={() => setEditing(true)}
            className="flex-row items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1.5 active:opacity-70"
          >
            <Pencil color="#52525B" size={13} />
            <Text className="text-xs font-medium text-textDim">Edit</Text>
          </Pressable>
        </View>

        <View className="mt-3">
          <Row label="Concentration" value={`${active.concentrationMgPerMl} mg/mL`} />
          <Row
            label="Default dose"
            value={`${active.schedule.defaultDoseMg} ${active.preset.defaultUnit}`}
          />
          <Row
            label="Cadence"
            value={`every ${(active.schedule.cadenceHours / 24).toFixed(2)} days`}
          />
          <Row label="Absorption t½" value={`${active.preset.absorptionHalfLifeHours} h`} />
          <Row label="Elimination t½" value={`${active.preset.eliminationHalfLifeHours} h`} />
          <Row
            label="Bioavailability"
            value={`${(active.preset.bioavailability * 100).toFixed(0)}%`}
          />
        </View>

        <View className="mt-8">
          <View className="mb-2 flex-row items-center justify-between">
            <Text className="text-[10px] font-medium uppercase tracking-wider text-textMuted">
              Compounds
            </Text>
            <Pressable
              onPress={handleAddCompound}
              className="flex-row items-center gap-1 rounded-md bg-surfaceHi px-2 py-1 active:opacity-70"
              hitSlop={4}
            >
              <Plus color="#52525B" size={12} />
              <Text className="text-[10px] font-medium text-textDim">Add</Text>
            </Pressable>
          </View>

          <View className="rounded-2xl border border-border bg-surface">
            {userCompounds.map((uc) => {
              const isActive = uc.id === active.id;
              return (
                <View
                  key={uc.id}
                  className="flex-row items-center gap-2 border-b border-border/60 p-3 last:border-b-0"
                >
                  <Pressable
                    disabled={submitting}
                    onPress={() => openEdit(uc.id)}
                    className="flex-1 flex-row items-center gap-2 active:opacity-70"
                  >
                    <View className="flex-1">
                      <View className="flex-row items-center gap-2">
                        <Text className="text-sm font-medium text-text">{uc.preset.name}</Text>
                        {hasMany && isActive && (
                          <View className="rounded-full bg-accent/10 px-1.5 py-0.5">
                            <Text className="text-[9px] font-semibold uppercase tracking-wide text-accent">
                              Showing
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text className="mt-0.5 text-[11px] text-textMuted">
                        {uc.schedule.defaultDoseMg}
                        {uc.preset.defaultUnit} every{' '}
                        {(uc.schedule.cadenceHours / 24).toFixed(1)}d ·{' '}
                        {uc.concentrationMgPerMl}mg/mL
                      </Text>
                    </View>
                    <Pencil color="#A1A1AA" size={13} />
                  </Pressable>
                  <Pressable
                    disabled={submitting}
                    onPress={() => handleDeleteCompound(uc.id)}
                    className="rounded-md bg-surfaceHi p-1.5 active:opacity-70"
                    hitSlop={6}
                  >
                    <Trash2 color="#DC2626" size={13} />
                  </Pressable>
                </View>
              );
            })}
          </View>
          <Text className="mt-2 text-[11px] text-textMuted">
            {hasMany
              ? 'Tap a compound to edit it — that also makes it the one shown across Home and Graph. Switch quickly anytime from the chip in the top-right.'
              : 'Tap to edit dose, cadence, dose time, and concentration.'}
          </Text>
        </View>

        <Text className="mb-1 mt-10 text-[10px] font-medium uppercase tracking-wider text-textMuted">
          Account
        </Text>
        <Row label="Email" value={user?.email ?? '—'} />
        <Pressable
          disabled={submitting}
          onPress={handleLogout}
          className="mt-4 flex-row items-center justify-center gap-2 rounded-xl border border-border bg-surface py-3 active:opacity-70"
        >
          <LogOut color="#52525B" size={14} />
          <Text className="text-sm font-medium text-textDim">Log out</Text>
        </Pressable>

        <McpSection email={user?.email ?? null} />

        <Text className="mb-1 mt-10 text-[10px] font-medium uppercase tracking-wider text-textMuted">
          Coming soon
        </Text>
        <Text className="text-sm text-textDim">
          Blood-test log · side-effect tracker · custom compounds · target curve tuning · export.
        </Text>

        <View className="mt-12 flex-row items-center justify-center gap-4">
          <Pressable
            onPress={() => navigate({ to: '/privacy' })}
            hitSlop={8}
            className="active:opacity-60"
          >
            <Text className="text-[11px] text-textMuted">Privacy</Text>
          </Pressable>
          <Text className="text-[11px] text-textMuted">·</Text>
          <Pressable
            onPress={() => navigate({ to: '/terms' })}
            hitSlop={8}
            className="active:opacity-60"
          >
            <Text className="text-[11px] text-textMuted">Terms</Text>
          </Pressable>
        </View>
        <Text className="mt-1 text-center text-[11px] text-textMuted">halvn v0.1</Text>
      </ScrollView>

      <EditCompoundSheet visible={editing} onClose={() => setEditing(false)} />
    </SafeAreaView>
  );
}

const MCP_URL = process.env.EXPO_PUBLIC_MCP_URL ?? 'https://halvn-mcp.xxlxx.workers.dev';

function base64url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256Hex(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function generateRawToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return `hlvn_${base64url(bytes)}`;
}

function McpSection({ email }: { email: string | null }) {
  const { user } = db.useAuth();
  const { data, error: queryError, refresh } = useServerQuery(
    user ? { mcpTokens: { $: { where: { 'owner.id': user.id } } } } : null,
  );
  const tokens = (data?.mcpTokens ?? []) as Array<{
    id: string;
    name: string;
    prefix: string;
    createdAt: string;
    lastUsedAt: string;
    revokedAt: string;
  }>;
  const activeTokens = tokens
    .filter((t) => !(t.revokedAt && new Date(t.revokedAt).getTime() > 0))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const mintIntents = useRef(new Map<string, { id: string; raw: string; tokenHash: string; createdAt: Date }>());
  const [creating, setCreating] = useState(false);
  const { submitting, error, submit } = useDoseSubmission(() => {});
  const [newName, setNewName] = useState('');
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    mintIntents.current.clear();
    setFreshToken(null);
    setCreating(false);
  }, [user?.id]);

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied(null), 1600);
    } catch {
      setCopied('error');
    }
  };

  const mintToken = () => {
    if (!user || queryError) return;
    void submit(async () => {
    const name = newName.trim() || 'Untitled token';
    const key = JSON.stringify([user.id, name]);
    let intent = mintIntents.current.get(key);
    if (!intent) {
      const raw = generateRawToken();
      intent = { id: makeId(), raw, tokenHash: await sha256Hex(raw), createdAt: new Date() };
      mintIntents.current.set(key, intent);
    }
    const attempt = intent;
    const fields = {
      tokenHash: attempt.tokenHash,
      prefix: attempt.raw.slice(0, 13),
      name,
      createdAt: attempt.createdAt,
      lastUsedAt: new Date(0),
      revokedAt: new Date(0),
    };
    await transactCreateServer([
      tx.mcpTokens[attempt.id]!.create(fields).link({ owner: user.id }),
    ], async () => {
      const result = await queryServer({ mcpTokens: { $: { where: { id: attempt.id } }, owner: {} } });
      const saved = result.mcpTokens[0];
      return !!saved && saved.owner?.id === user.id && matchesSavedFields(saved, fields);
    });
    await refresh();
    mintIntents.current.delete(key);
    const raw = attempt.raw;
    if ((await db.getAuth())?.id !== user.id) return;
    setFreshToken(raw);
    setCreating(false);
    setNewName('');
    });
  };

  const revokeToken = (id: string) => {
    if (queryError) return;
    void submit(async () => {
      await transactServer([tx.mcpTokens[id]!.update({ revokedAt: new Date() }, { upsert: false })]);
      await refresh();
    });
  };

  const tokenForSnippet = freshToken ?? '<your-token>';
  const cliCommand = `claude mcp add --transport http halvn ${MCP_URL} --header "Authorization: Bearer ${tokenForSnippet}"`;
  const configSnippet = JSON.stringify(
    {
      mcpServers: {
        halvn: {
          type: 'http',
          url: MCP_URL,
          headers: {
            Authorization: `Bearer ${tokenForSnippet}`,
          },
        },
      },
    },
    null,
    2,
  );

  return (
    <View>
      <Text className="mb-1 mt-10 text-[10px] font-medium uppercase tracking-wider text-textMuted">
        Connect to Claude (MCP)
      </Text>
      <View className="rounded-2xl border border-border bg-surface p-4">
        <View className="flex-row items-start gap-2">
          <View className="mt-0.5 h-8 w-8 items-center justify-center rounded-lg bg-accent/10">
            <Terminal color="#FF5A1F" size={16} />
          </View>
          <View className="flex-1">
            <Text className="text-sm font-semibold text-text">Talk to halvn from Claude</Text>
            <Text className="mt-1 text-xs leading-relaxed text-textDim">
              Mint a token, paste it into your MCP client, and Claude can log doses, list your
              schedule, and edit entries — all scoped to {email ?? 'your account'}.
            </Text>
          </View>
        </View>

        {(error || queryError) && <Text role="alert" className="mt-3 text-sm text-danger">{error ?? queryError?.message}</Text>}

        {/* Fresh token banner */}
        {freshToken && (
          <View className="mt-4 rounded-xl border border-accent/40 bg-accent/[0.06] p-3">
            <Text className="text-[10px] font-medium uppercase tracking-wider text-accent">
              Copy now — shown once
            </Text>
            <View className="mt-1.5 flex-row items-center gap-2 rounded-lg bg-surface px-3 py-2">
              <Text className="flex-1 text-[11px] text-text" numberOfLines={1} style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                {freshToken}
              </Text>
              <Pressable
                onPress={() => copy('token', freshToken)}
                className="rounded-md bg-surfaceHi p-1.5 active:opacity-70"
                hitSlop={6}
              >
                <Copy color="#52525B" size={13} />
              </Pressable>
            </View>
            <Text className="mt-1.5 text-[10px] text-textMuted">
              We only store its hash. If you lose it, mint a new one.
            </Text>
            <Pressable
              onPress={() => setFreshToken(null)}
              className="mt-2 self-start active:opacity-60"
              hitSlop={6}
            >
              <Text className="text-[11px] text-textDim underline">I've copied it, dismiss</Text>
            </Pressable>
          </View>
        )}

        {/* Token list */}
        <View className="mt-4">
          <View className="mb-2 flex-row items-center justify-between">
            <Text className="text-[10px] font-medium uppercase tracking-wider text-textMuted">
              Your tokens ({activeTokens.length})
            </Text>
            {!creating && (
              <Pressable
                disabled={submitting || !!queryError}
                onPress={() => setCreating(true)}
                className="flex-row items-center gap-1 rounded-md bg-surfaceHi px-2 py-1 active:opacity-70"
                hitSlop={4}
              >
                <Plus color="#52525B" size={12} />
                <Text className="text-[10px] font-medium text-textDim">New token</Text>
              </Pressable>
            )}
          </View>

          {creating && (
            <View className="mb-3 rounded-lg border border-border bg-bg p-2.5">
              <Text className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-textMuted">
                Name (e.g. "MacBook", "iPhone")
              </Text>
              <TextInput
                value={newName}
                editable={!submitting}
                onChangeText={setNewName}
                placeholder="MacBook"
                placeholderTextColor="#A1A1AA"
                onSubmitEditing={mintToken}
                className="rounded-md border border-border bg-surface px-2.5 py-2 text-sm text-text outline-none"
              />
              <View className="mt-2 flex-row gap-2">
                <Pressable
                  disabled={submitting || !!queryError}
                  onPress={mintToken}
                  className="flex-1 items-center rounded-md bg-accent py-2 active:opacity-80"
                >
                  <Text className="text-xs font-medium text-white">{submitting ? 'Saving…' : 'Create'}</Text>
                </Pressable>
                <Pressable
                  disabled={submitting}
                  onPress={() => { setCreating(false); setNewName(''); }}
                  className="flex-1 items-center rounded-md border border-border bg-surface py-2 active:opacity-70"
                >
                  <Text className="text-xs font-medium text-textDim">Cancel</Text>
                </Pressable>
              </View>
            </View>
          )}

          {!queryError && activeTokens.length === 0 && !creating && (
            <Text className="py-2 text-xs text-textMuted">No tokens yet. Click "New token" above.</Text>
          )}

          {activeTokens.map((t) => {
            const lastUsedMs = new Date(t.lastUsedAt).getTime();
            const lastUsed = lastUsedMs > 0
              ? new Date(t.lastUsedAt).toLocaleDateString()
              : 'never';
            return (
              <View key={t.id} className="flex-row items-center gap-2 border-b border-border/60 py-2.5 last:border-b-0">
                <View className="flex-1">
                  <Text className="text-sm font-medium text-text">{t.name}</Text>
                  <Text className="mt-0.5 text-[11px] text-textMuted" style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                    {t.prefix}… · last used {lastUsed}
                  </Text>
                </View>
                <Pressable
                  disabled={submitting || !!queryError}
                  onPress={() => revokeToken(t.id)}
                  className="rounded-md bg-surfaceHi p-1.5 active:opacity-70"
                  hitSlop={6}
                >
                  <Trash2 color="#DC2626" size={13} />
                </Pressable>
              </View>
            );
          })}
        </View>

        {/* Endpoint + config snippet */}
        <View className="mt-4">
          <Text className="mb-1 text-[10px] font-medium uppercase tracking-wider text-textMuted">
            MCP endpoint
          </Text>
          <View className="flex-row items-center gap-2 rounded-lg bg-bg px-3 py-2">
            <Text className="flex-1 text-xs text-text" numberOfLines={1} style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
              {MCP_URL}
            </Text>
            <Pressable
              onPress={() => copy('url', MCP_URL)}
              className="rounded-md bg-surfaceHi p-1.5 active:opacity-70"
              hitSlop={6}
            >
              <Copy color="#52525B" size={13} />
            </Pressable>
          </View>
        </View>

        <View className="mt-3">
          <View className="mb-1 flex-row items-center justify-between">
            <Text className="text-[10px] font-medium uppercase tracking-wider text-textMuted">
              Claude Code — one command
            </Text>
            <Pressable
              onPress={() => copy('command', cliCommand)}
              className="flex-row items-center gap-1 rounded-md bg-surfaceHi px-2 py-1 active:opacity-70"
              hitSlop={4}
            >
              <Copy color="#52525B" size={11} />
              <Text className="text-[10px] font-medium text-textDim">Copy</Text>
            </Pressable>
          </View>
          <View className="rounded-lg bg-bg p-3">
            <Text className="text-[11px] leading-relaxed text-textDim" style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
              {cliCommand}
            </Text>
          </View>
          <Text className="mt-1.5 text-[10px] text-textMuted">
            Run in your terminal. Adds halvn to Claude Code — no local install, Claude talks
            directly to the hosted server.
          </Text>
        </View>

        <View className="mt-3">
          <View className="mb-1 flex-row items-center justify-between">
            <Text className="text-[10px] font-medium uppercase tracking-wider text-textMuted">
              Or paste into .mcp.json
            </Text>
            <Pressable
              onPress={() => copy('config', configSnippet)}
              className="flex-row items-center gap-1 rounded-md bg-surfaceHi px-2 py-1 active:opacity-70"
              hitSlop={4}
            >
              <Copy color="#52525B" size={11} />
              <Text className="text-[10px] font-medium text-textDim">Copy</Text>
            </Pressable>
          </View>
          <View className="rounded-lg bg-bg p-3">
            <Text className="text-[11px] leading-relaxed text-textDim" style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
              {configSnippet}
            </Text>
          </View>
          <Text className="mt-1.5 text-[10px] text-textMuted">
            Project <Text className="text-textDim">.mcp.json</Text> or global{' '}
            <Text className="text-textDim">~/.claude.json</Text>. Other MCP clients (Cursor,
            Claude Desktop) use the same block.
          </Text>
        </View>

        {copied && (
          <Text className="mt-2 text-[11px] text-success">
            {copied === 'error' ? 'Copy failed — clipboard blocked.' : `Copied ${copied}.`}
          </Text>
        )}
      </View>
    </View>
  );
}
